import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Alert, Button, Stack, Text } from '@mantine/core';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    Sentry.captureException(error, {
      extra: errorInfo,
      tags: {
        component: 'ErrorBoundary'
      }
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Stack align="center" justify="center" h="100vh" gap="md">
          <Alert
            title="Something went wrong"
            color="red"
            variant="filled"
            w="100%"
            maw={400}
          >
            <Stack gap="sm">
              <Text size="sm">
                We've been notified and are working to fix the issue.
              </Text>
              <Button
                variant="white"
                color="red"
                onClick={this.handleReset}
                size="sm"
              >
                Try Again
              </Button>
            </Stack>
          </Alert>
        </Stack>
      );
    }

    return this.props.children;
  }
} 